import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";
import { serviceSchema } from "@/data/schema";
import CtaBanner from "@/components/CtaBanner";

export default function SalesEvents() {
  const schema = serviceSchema({
    name: "Marketplace Sale Event Preparation",
    description: "Preparation, execution and post-event analysis for marketplace sale events such as Big Billion Days, Amazon Great Indian Festival and Prime Day.",
    path: "/sales-events",
    serviceType: "E-commerce sale event management",
  });

  return (
    <>
      <SEO
        title="E-commerce Sales Event Preparation & Marketplace Growth"
        description="Big Billion Days, Amazon Great Indian Festival, Republic Day sales, Pink Friday and quick-commerce sale days — end-to-end preparation, execution and post-event analysis."
        keywords="marketplace sales event India, Big Billion Days preparation, Amazon Great Indian Festival, festive sale India"
        path="/sales-events"
        schema={schema}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Sales Events", path: "/sales-events" }]}
      />

      <section className="border-b-2 border-black bg-[#FFD700] py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="tape-dark">Sales Events</div>
          <h1 className="display mt-6 max-w-4xl text-4xl font-black leading-[1.02] tracking-tight md:text-5xl lg:text-6xl">
            E-commerce Sales Event Preparation & Marketplace Growth
          </h1>
          <p className="mt-5 max-w-3xl text-base text-black/85 md:text-lg">
            30-45% of annual Indian D2C GMV lands in a handful of sale windows. Orina Enterprise plans and executes the full preparation cycle — catalog, inventory, pricing, ads, creatives and post-event analysis.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact/?type=expert" className="btn-brutal">Prepare My Brand for the Next Sales Event <ArrowUpRight size={16} /></Link>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <h2 className="display text-3xl font-black md:text-4xl">What we cover</h2>
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {[
              "Catalog readiness — hero SKU refresh, variants under parent",
              "Inventory planning — 2.5-3× baseline stock, safety stock",
              "Pricing & offers — MRP discipline, coupon stacks, bank offers",
              "Ads pre-priming — 21-day relevance-score push before event",
              "A+ content refresh — sale-period messaging modules",
              "Product images & festive overlays",
              "Backend keyword refresh — festive intent capture",
              "Marketplace participation — Amazon GIF, Flipkart BBD, JioMart Festive Days, Myntra EOSS, Nykaa Pink Friday",
              "Quick-commerce sale days — Blinkit, Zepto, Instamart",
              "Post-event analysis — SKU velocity, new-to-brand share, ROAS"
            ].map((s) => (
              <div key={s} className="flex items-start gap-3 border-2 border-black bg-[#FAFAF7] p-4">
                <CheckCircle2 className="mt-1 shrink-0 text-[#25D366]" size={18} />
                <span className="text-sm md:text-base">{s}</span>
              </div>
            ))}
          </div>
          <div className="mt-10 grid gap-4 border-2 border-black bg-[#FFD700] p-6 md:grid-cols-4">
            <Link to="/tools/listing-score/" className="text-sm font-bold uppercase tracking-widest underline hover:no-underline">Score my listing →</Link>
            <Link to="/tools/marketplace-calculators/" className="text-sm font-bold uppercase tracking-widest underline hover:no-underline">Model event profit →</Link>
            <Link to="/services/a-plus-listing-graphics/" className="text-sm font-bold uppercase tracking-widest underline hover:no-underline">A+ graphics refresh →</Link>
            <Link to="/services/ecommerce-video-creation/" className="text-sm font-bold uppercase tracking-widest underline hover:no-underline">Festive video creatives →</Link>
          </div>
        </div>
      </section>

      <CtaBanner title="Book your sales event slot" subtitle="Slots are limited before every big Indian sale window — talk to us early." />
    </>
  );
}
